const {
    find, fill, get, remove, isUndefined, groupBy, map, filter, isNull,
} = require('lodash');
const { mergeInspirationTiles } = require('./merge-inspiration-tiles');

const GHOST_TILE_ITEM = {
    type: 'ghost',
};

const TealiumEventBuilder = require('../../analytics/tealium/builders/tealium-event-builder');

class TileListViewController {
    constructor($q, $timeout, $location, $transclude, $state, serializeAsync, localStorageWrapper, moveFocus, settingsByBreakpointService, shopPageStateService, inspirationService, sortByKeys, userPreferenceService, tileListViewLayout, trolleyService, categoriesService, analyticsService, productGroupService, settings) {
        this.$q = $q;
        this.$timeout = $timeout;
        this.$location = $location;
        this.$transclude = $transclude;
        this.$state = $state;
        this.localStorageWrapper = localStorageWrapper;
        this.moveFocus = moveFocus;
        this.settingsByBreakpointService = settingsByBreakpointService;
        this.shopPageStateService = shopPageStateService;
        this.inspirationService = inspirationService;
        this.sortByKeys = sortByKeys;
        this.userPreferenceService = userPreferenceService;
        this.tileListViewLayout = tileListViewLayout;
        this.trolleyService = trolleyService;
        this.categoriesService = categoriesService;
        this.analyticsService = analyticsService;
        this.productGroupService = productGroupService;
        this.settings = settings;

        this.loadTilesSerialized = serializeAsync(this.loadTiles, this);
    }

    $onInit() {
        this.isPaginationEnabled = isUndefined(this.isPaginationEnabled) ? true : this.isPaginationEnabled;
        this.isListViewGroupByEnabled = isUndefined(this.isListViewGroupByEnabled) ? false : this.isListViewGroupByEnabled;
        this.isListViewLayoutEnabled = isUndefined(this.isListViewLayoutEnabled) ? false : this.isListViewLayoutEnabled;

        this.listViewGroupBy = false;
        this.listViewLayout = this.tileListViewLayout.LIST_VIEW;
        this.listViewVisited = true;
        this.listViewGroupByVisited = true;

        if (this.isListViewGroupByEnabled) {
            this.groupToggleState = {};
        }

        this.defaultPageSize = this.settingsByBreakpointService.getSettingValueByBreakpoint('OneSite', {
            largeFormat: 'LargeFormatPageSize',
            mediumFormat: 'MediumFormatPageSize',
            smallFormat: 'SmallFormatPageSize',
        });

        if (this.sortOptions) {
            this.sortOptions.sortContext = this.sortOptions.sortContext === undefined
                ? this.sortByKeys.DEFAULT_KEY
                : this.sortOptions.sortContext;
        }

        this.items = [];
        this.shouldGetInspirationCards = this.shouldGetInspirationCards !== false;
        this.categoryOrder = map(this.categoriesService.rootCategory.Children, 'Description');
        this.categoryOrder.push('Bundles');
        this.categoryOrder.push('Other');

        if (this.isListViewLayoutEnabled || this.isListViewGroupByEnabled) {
            this.userPreferenceService.getUserPreference().then((userPreferences) => {
                if (this.isListViewLayoutEnabled === true) {
                    this.listViewLayout = userPreferences.ListViewLayout;
                    this.listViewVisited = userPreferences.ListViewVisited;
                    if (isNull(this.listViewLayout) || this.listViewLayout === '') {
                        this.listViewLayout = this.tileListViewLayout.LIST_VIEW;
                    }
                    if (this.listViewVisited === false) {
                        this.userPreferenceService.setListViewVisited(true);
                    }
                }

                if (this.isListViewGroupByEnabled === true) {
                    this.listViewGroupBy = userPreferences.ListViewGroupBy;
                    this.listViewGroupByVisited = userPreferences.ListViewGroupByVisited;
                    if (this.listViewGroupByVisited === false) {
                        this.userPreferenceService.setListViewGroupByVisited(true);
                    }
                }

                this.update();
            });
        } else {
            this.update();
        }
    }

    isMobile() {
        const x = window.innerWidth || document.documentElement.clientWidth || document.getElementsByTagName('body')[0].clientWidth;
        return (x < 850 && x > 767) || x < 600;
    }

    pageSize() {
        return this.listViewGroupBy ? 100 : this.defaultPageSize;
    }

    $onChanges(changes) {
        this.shouldRememberSort = isUndefined(this.shouldRememberSort) ? true : this.shouldRememberSort;

        if ((changes.sortBy || changes.sortOptions) && this.sortOptions != null) {
            this.sortType = this.getSortType();
        }

        if (
            (changes.filter && !changes.filter.isFirstChange()) ||
            (changes.sortBy && !changes.sortBy.isFirstChange()) ||
            (changes.pageNumber && !changes.pageNumber.isFirstChange())
        ) {
            this.update().then(() => this.focusOnFirstTile());
        }
    }

    saveListViewGroupBy(value) {
        this.userPreferenceService.setListViewGroupBy(value);
        this.listViewGroupByVisited = true;
        const event = new TealiumEventBuilder()
            .withEventName('link_click')
            .withLabel('List Grouping')
            .withValue(value ? 'Group' : 'Ungroup')
            .withEventType(this.analyticsService.mapperTypes.shop.userPreference)
            .withTrackingType('link')
            .build();
        this.analyticsService.push(event);
        this.pageNumber = 1;
        this.$state.go('.', { pageNumber: 1 });
        this.update();
    }

    saveListViewLayout(value) {
        this.userPreferenceService.setListViewLayout(value);
        this.listViewVisited = true;
        this.listViewLayout = value;
        const event = new TealiumEventBuilder()
            .withEventName('link_click')
            .withLabel('List View Type')
            .withValue(value === 'Tile' ? 'Grid' : 'List')
            .withEventType(this.analyticsService.mapperTypes.shop.userPreference)
            .withTrackingType('link')
            .build();
        this.analyticsService.push(event);
        this.pageNumber = 1;
        this.$state.go('.', { pageNumber: 1 });
        this.update();
    }

    requireListViewLayout() {
        if (!this.isListViewLayoutEnabled) {
            return false;
        }

        if (isNull(this.listViewLayout)) {
            return true;
        }

        if (this.isMobile()) {
            return false;
        }

        return this.listViewLayout === this.tileListViewLayout.LIST_VIEW;
    }

    addGroup(groupName) {
        const group = find(this.groupedItems, ['key', groupName]);
        const products = map(group.purchasable, 'data.product');
        this.trolleyService.addItemsToTrolley(products);
        const event = new TealiumEventBuilder()
            .withEventName('link_click')
            .withLabel(`Add ${groupName} Group`)
            .withEventType(this.analyticsService.mapperTypes.shop.userPreference)
            .withTrackingType('link')
            .build();
        this.analyticsService.push(event);
    }

    initializeGroupToggleState() {
        this.groupedKeys.forEach((key) => {
            if (this.groupToggleState[key] == null) {
                this.groupToggleState[key] = true;
            }
        });
    }

    toggleGroup(groupKey) {
        this.groupToggleState[groupKey] = !this.groupToggleState[groupKey];
    }

    isGroupExpanded(groupKey) {
        return this.groupToggleState[groupKey];
    }

    getSortType() {
        // Priority: URL -> Local Storage -> First one in list
        return this.shouldRememberSort
            ? find(this.sortOptions.sortTypes, { Value: this.sortBy }) ||
                find(this.sortOptions.sortTypes, { Value: this.localStorageWrapper.get(this.sortOptions.sortContext) }) ||
                this.sortOptions.sortTypes[0]
            : find(this.sortOptions.sortTypes, { Value: this.sortBy }) ||
                this.sortOptions.sortTypes[0];
    }

    getGroupType(item) {
        if (item.data.product.IsBundle) {
            return 'Bundles';
        }

        const department = this.productGroupService.getDepartmentGroup(
            JSON.parse(item.data.product.AdditionalAttributes.PiesProductDepartmentsjson),
            this.settings.lists.includedDepartmentIds,
        );

        return department;
    }

    groupItems() {
        const groupedObjects = groupBy(this.items, b => this.getGroupType(b));
        this.groupedKeys = Object.keys(groupedObjects);
        this.groupedItems = this.groupedKeys.map(key => ({ key, value: groupedObjects[key], purchasable: filter(groupedObjects[key], t => t.data.product.IsPurchasable) }));
        // order the category groupings the same as the navigation header.
        this.groupedItems = this.groupedItems.sort((a, b) => this.categoryOrder.indexOf(a.key) - this.categoryOrder.indexOf(b.key));
    }

    update() {
        const isAlreadyLoadingTiles = this.isLoadingTiles;
        this.isLoadingTiles = true;

        return this.loadTilesSerialized(!isAlreadyLoadingTiles)
            .then(([{ items, totalRecordCount, aggregations }, { Cards: inspiration }]) => {
                this.items = mergeInspirationTiles(items, inspiration, { pageNumber: this.pageNumber, pageSize: this.pageSize() });
                if (this.isListViewGroupByEnabled) {
                    if (this.listViewGroupBy) {
                        this.groupItems();
                        this.initializeGroupToggleState();
                    }
                }
                this.totalRecordCount = totalRecordCount;
                this.availableFilters = aggregations;
                this.filter = this.$state.params && this.$state.params.filter ? this.$state.params.filter : { filters: [] };
                this.isFilterApplied = this.$state.params && this.$state.params.filter;
                this.pushContentToAnalytics({
                    contentData: {
                        items: this.items,
                        totalRecordCount: this.totalRecordCount,
                        sortType: this.sortType,
                        inspirationCards: inspiration || [],
                        pageNumber: this.pageNumber,
                        filters: this.filter,
                        viewType: this.listViewLayout,
                        groupType: this.listViewGroupBy ? 'Grouped' : 'Ungrouped',
                    },
                });
            })
            .finally(() => {
                this.isLoadingTiles = false;
                this.shouldShowControlBar = this.sortOptions || this.$transclude.isSlotFilled('topRightSlot');
            });
    }

    loadTiles(shouldGenerateGhostTiles = true) {
        return this.$q.all([
            this.getTiles({
                pageSize: this.pageSize(), pageNumber: this.pageNumber, sortType: get(this.sortType, 'Value'), filter: this.filter,
            }),
            this.shouldGetInspirationCards ? this.inspirationService.getCardsForUrl(this.$location.pathAndSearch()) : { Cards: [] },
            shouldGenerateGhostTiles ? this.generateGhostTiles() : null,
        ]);
    }

    generateGhostTiles() {
        // -1 from the page size is more aesthetically pleasing
        const amountOfGhostTiles = this.totalRecordCount ? Math.min(this.totalRecordCount, this.pageSize() - 1) : this.pageSize() - 1;

        // The timeout is needed to ensure that the animations run
        this.items = [];
        return this.$timeout(() => {
            this.items = fill(Array(amountOfGhostTiles), GHOST_TILE_ITEM);
        });
    }

    onSortByChanged(option) {
        this.sortBy = option.Value;
        if (this.shouldRememberSort) {
            this.localStorageWrapper.set(this.sortOptions.sortContext, this.sortBy);
        }
        this.pageNumber = 1;
        this.$state.go('.', { sortBy: option.Value, pageNumber: undefined });
    }

    onPageSelected() {
        this.shopPageStateService.resetCenterPanelScroll();
    }

    focusOnFirstTile() {
        return this.moveFocus('.tileList-tile:first-child');
    }

    onRemove(item) {
        const removeItemPromise = this.onRemoveItem ? this.onRemoveItem({ item }) : this.$q.when();

        remove(this.items, item);
        if (this.isListViewGroupByEnabled) {
            if (this.listViewGroupBy) {
                this.groupItems();
                this.initializeGroupToggleState();
            }
        }

        if (this.items.length === 0 && this.pageNumber > 1) {
            removeItemPromise.then(() => {
                this.$state.go('.', { pageNumber: this.pageNumber - 1 }, { reload: true });
            });
        }
    }

    get hasFilters() {
        return this.availableFilters && this.availableFilters.length > 0;
    }
}

TileListViewController.$inject = ['$q', '$timeout', '$location', '$transclude', '$state', 'serializeAsync', 'LocalStorageWrapper', 'MoveFocus', 'SettingsByBreakpointService', 'ShopPageStateService', 'InspirationService', 'SortByKeys', 'UserPreferenceService', 'TileListViewLayout', 'TrolleyService', 'CategoriesService', 'AnalyticsService', 'ProductGroupService', 'Settings'];

const tileListViewComponentDefinition = {
    bindings: {
        listTitle: '<?',
        pageNumber: '<?',
        filter: '<?',
        totalRecordCount: '<',
        sortBy: '<?',
        sortOptions: '<?',
        shouldGetInspirationCards: '<?',
        isEditing: '<?',
        onRemoveItem: '&?',
        getTiles: '&',
        isPaginationEnabled: '<?',
        shouldRememberSort: '<?',
        pushContentToAnalytics: '&',
        noResultsTerm: '<?',
        breadcrumbs: '<?',
        isListViewGroupByEnabled: '<?',
        isListViewLayoutEnabled: '<?',
    },
    controller: TileListViewController,
    transclude: {
        topRightSlot: '?topRight',
        zeroResultSlot: '?zeroResult',
        controlSlot: '?control',
    },
    template: require('./tile-list-view.component.html').trim(),
};

module.exports = {
    TileListViewController,
    tileListViewComponentDefinition,
};



// WEBPACK FOOTER //
// ./src/shop/tiles/tile-list-view.component.js